import { useAuthFetch, useGuardedEffect } from "@p0security/frontend-core";
import { ErrorDisplay } from "@p0security/frontend-core/react";
import { DirectedGraph } from "@p0security/shared/graph/types";
import {
  ALL_SCOPE_SENTINEL,
  AssessmentScopeIntegration,
  ProviderOrAll,
  scopeToProvider,
  toIntegration,
  toKey,
  toScope,
} from "@p0security/shared/types/assessment";
import { AssessmentNodes } from "@p0security/shared/types/assessment/data/index";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { useObjectMemo } from "hooks/useObjectMemo";
import { isArray, noop } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { StringParam, UrlUpdateType, useQueryParam } from "use-query-params";

import { GraphProcessingStep } from "../components/GraphStep";
import { CachedMetagraph, fetchGraphForScope } from "../utils/graph";

type ScopeContext = {
  graph: DirectedGraph<AssessmentNodes> | undefined;
  integration: AssessmentScopeIntegration | "all";
  percentage: number;
  provider: ProviderOrAll;
  /** Clears and reloads graph */
  reset: () => void;
  scopeKey: string;
  setScopeKey: (scope: string, updateType?: UrlUpdateType) => void;
  step: GraphProcessingStep;
  validScopeKeys: Set<string>;
};

export const ScopeContext = createContext<ScopeContext>({
  graph: undefined,
  integration: ALL_SCOPE_SENTINEL,
  percentage: 0,
  provider: ALL_SCOPE_SENTINEL,
  reset: noop,
  scopeKey: ALL_SCOPE_SENTINEL,
  setScopeKey: noop,
  step: "loading" as const,
  validScopeKeys: new Set(ALL_SCOPE_SENTINEL),
});

export const ScopeProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { details, last } = useContext(SelectedEnvironmentContext);
  const assessmentId = details?.assessmentId;
  const [error, setError] = useState<string>();
  const authFetch = useAuthFetch(setError);
  const [graph, setGraph] = useState<DirectedGraph<AssessmentNodes>>();
  // Cache metagraph to save large downloads / data processing.
  // Only the metagraph as we could consume too much memory with single-target
  // caches.
  const [metaGraph, setMetaGraph] = useState<CachedMetagraph>();
  const [step, setStep] = useState<GraphProcessingStep>("requested");
  const [percentage, setPercentage] = useState<number>(0);
  const [scopeKey, setScopeKey] = useQueryParam("scope", StringParam);

  const effectiveScopeKey = useMemo(
    () => scopeKey || ALL_SCOPE_SENTINEL,
    [scopeKey]
  );

  const onError = useCallback((error: any) => {
    console.error(error);
    setError(error);
  }, []);

  const validScopeKeys = useMemo(() => {
    const valid = new Set<string>([ALL_SCOPE_SENTINEL]);
    if (!isArray(last.doc?.data.scope)) return valid;
    for (const scope of last.doc?.data.scope ?? []) {
      valid.add(toKey(scope));
    }
    return valid;
  }, [last.doc?.data.scope]);

  // Download and boost graph when:
  // - A new environment is selected
  // - An assessment job completes
  // - The selected scope changes
  //
  // Caches the metagraph so that reselecting "All targets" is quick.
  useGuardedEffect(
    (cancellation) => async () => {
      if (!assessmentId || !last.doc) return;
      if (last.doc.data.assessmentId !== assessmentId) {
        setGraph(undefined);
        setStep("requested");
        return;
      }

      setGraph(undefined);

      // If scope is all and there's an up-to-date cached metagraph, use that
      if (
        metaGraph?.assessmentId === assessmentId &&
        metaGraph?.jobId === last.doc?.id &&
        effectiveScopeKey === ALL_SCOPE_SENTINEL
      ) {
        setGraph(metaGraph.graph);
        setStep("aggregating");
        return;
      }

      cancellation.onCancel = () => {
        setGraph(graph);
        setStep(step);
      };

      fetchGraphForScope({
        assessmentId,
        authFetch,
        cancellation,
        effectiveScopeKey,
        lastJobId: last.doc.id,
        setGraph,
        setMetaGraph,
        setPercentage,
        setStep,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- previous values ('graph', 'lastGraphScopeKey', and 'step') in dependencies lead to infinite render loop. We set these again inside the hook.
    [
      assessmentId,
      authFetch,
      effectiveScopeKey,
      last.doc,
      metaGraph?.assessmentId,
      metaGraph?.jobId,
    ],
    onError
  );

  const scope = toScope(effectiveScopeKey);
  const integration = toIntegration(scope);

  const reset = useCallback(() => setGraph(undefined), []);

  const value = useObjectMemo<ScopeContext>({
    graph,
    integration,
    percentage,
    provider: scopeToProvider(integration),
    reset,
    scopeKey: effectiveScopeKey,
    setScopeKey,
    step,
    validScopeKeys,
  });

  return (
    <ScopeContext.Provider value={value}>
      {error && (
        <ErrorDisplay title="Error loading environment" error={String(error)} />
      )}
      {children}
    </ScopeContext.Provider>
  );
};
